import { Activity, GraphGroup, GROUP_SHOW_TYPE, TAGS } from "components/planner/types"
import { activitiesSetOverlap, groupsHeightForEach } from "components/planner/utils"
import { getGrowUnitName } from "stores/growUnit/utils"
import { getGrowUnitLayerName, getLayersDesired } from "stores/growUnit/utils/layer"
import { groupLayersByPhases } from "stores/phase/layers"
import { getPhaseLabel } from "stores/phase/utils"
import { Cycle } from "types/Cycle"
import { GrowUnit, GrowUnitLayer } from "types/GrowUnit"
import { groupBy } from "utils/array"
import { activitiesFromCycles } from "../utils/cycle"



/** Crea un generico GROWINGS-GROUP: rappresentazione sul PLANNER di piu' GROWINGS co i loro LAYERS*/
export function buildGrowUnitsGroup(
	growUnits: GrowUnit[],
	cycles: Cycle[],
	showLayersDisabled: boolean,
	/** se definito, filtra mostrando solo i layer specificati "layer_number"+ "growunit_id" */
	onlyLayers?: Set<string>,
): GraphGroup {

	// preparo il GRAPH-GROUP ROOT che conterrà tutte le GROW-UNITs
	const growingGroup: GraphGroup = {
		name: "planner_root",
		title: "GROW UNITS",
		collapsed: true,
		groups: []
	}

	// ciclo tutte le GROW-UNIT e creo i relativi GRAPH-GROUP
	for (const growUnit of growUnits) {

		// i GROWUNIT-LAYER desiderati
		const allLayers = getLayersDesired(growUnit, onlyLayers)
		const desPhasesLayer = groupLayersByPhases(allLayers, showLayersDisabled)

		// elimino le PHASE senza LAYER e li ordino (inverso rispetto a "layerNumber")
		const phasesLayer = Object.keys(desPhasesLayer)
			.reduce((acc, phase) => {
				let layers = desPhasesLayer[phase]
				if (layers?.length > 0) {
					acc[phase] = layers.sort((l1, l2) => l2.layerNumber - l1.layerNumber)
				}
				return acc
			}, {} as { [phase: string]: GrowUnitLayer[] })

		// dai CYCLES ricavo le ACTVITIES da inserire nella GROW-UNIT
		const acts = activitiesFromCycles(cycles, growUnit.id)

		// controllo non ci siano sovrapposizioni tra le ACTIVITYs (es. per i cicli a più PHASE) e se si le setto
		activitiesSetOverlap(acts)

		// trasforma le ACTIVITIES in Groups
		const grps = createPhasesGroup(
			acts,
			phasesLayer,
			layer => getGrowUnitLayerName(growUnit, layer)
		)

		// creo il GROUP GROW-UNIT e lo inserisco nel SUPER-GROUP GROWING
		const groups: GraphGroup = {
			name: growUnit.growUnitCode ?? "unknow",
			title: getGrowUnitName(growUnit),
			groups: grps,
		}
		growingGroup.groups.push(groups)
	}

	// tutto compatto e bello
	groupsHeightForEach([growingGroup], null, (g) => {
		g.show = GROUP_SHOW_TYPE.COMPACT
	})

	// messo in ordine alfebetico
	growingGroup.groups = growingGroup.groups.sort((g1, g2) => g1.title.localeCompare(g2.title))

	return growingGroup
}

/**
 * crea una GraphGroup per il PLANNER   
 * raggruppati per PHASE
 */
export function buildPhasesAndGrowUnitGroup(
	growUnits: GrowUnit[],
	cycles: Cycle[],
	showLayersDisabled: boolean,
	/** se definito, filtra mostrando solo i layer specificati "layer_number"+ "growunit_id" */
	onlyLayers?: Set<string>,
): GraphGroup {

	// normalizzo tutti i LAYERS
	let allLayers = growUnits.flatMap(gu => getLayersDesired(gu, onlyLayers))

	// raggruppo i LAYERs per PHASE
	const phasesPerLayers = groupLayersByPhases(allLayers, showLayersDisabled)

	// creo i GRAPH-GROUPs per le PHASES
	const phasesGraphGroup = Object.entries(phasesPerLayers).map(([phase, layers]) => {

		// raggruppo per GROW-UNIT
		const guGroup = groupBy(layers, layer => layer.growUnitId.toString())

		// crea i GRAPH-GROUP per le GROW-UNITs
		const gusGraphGroups = Object.entries(guGroup).map(([guId, layers]) => {
			if (!layers || layers.length == 0) return null

			const gu = growUnits.find(gu => gu.id == layers[0].growUnitId)
			const sortedLayers = layers.sort((l1, l2) => l2.layerNumber - l1.layerNumber)
			const activities = activitiesFromCycles(cycles, gu.id)
			const guGroup = createPhaseGroup(
				activities,
				phase,
				sortedLayers,
				layer => getGrowUnitLayerName(gu, layer)
			)
			guGroup.name = gu.growUnitCode
			guGroup.title = getGrowUnitName(gu)
			return guGroup
		}).filter(group => group)

		// il GROUP della "phase"
		const phaseGraphGroup: GraphGroup = {
			name: phase,
			title: phase,
			collapsed: false,
			noCompact: false,

			groups: gusGraphGroups,
		}

		return phaseGraphGroup
	})

	const phaseRootGraphGroup: GraphGroup = {
		name: "planner_root",
		title: "PHASES",
		collapsed: true,
		groups: phasesGraphGroup,
	}
	// tutto compatto e bello
	groupsHeightForEach([phaseRootGraphGroup], null, (g) => {
		g.show = GROUP_SHOW_TYPE.COMPACT
	})

	return phaseRootGraphGroup
}

/**
 * Da una serie di ACTIVITY  
 * e specificando i LAYER per ogni PHASES  
 * restituisco le rappresentazioni GRAPH-GOUP 
 */
function createPhasesGroup(
	/** le ACTIVITY */
	activities: Activity[] = [],
	/** per ogni PHASE tutti i LAYER da prendere in considerazione*/
	phasesLayer: { [phase: string]: GrowUnitLayer[] },
	/** funzione utile per ricavare il nome del layer*/
	fnGetName: (gul: GrowUnitLayer) => string
): GraphGroup[] {
	const phases = Object.keys(phasesLayer)
	const ret = phases.map(phase => {
		const layers = phasesLayer[phase]
		if (!phase || !layers || layers.length == 0) return null
		return createPhaseGroup(activities, phase, layers, fnGetName)
	}).filter(group => group)
	return ret
}

function createPhaseGroup(
	activities: Activity[],
	phase: string,
	layers: GrowUnitLayer[],
	/** funzione utile per ricavare il nome del layer*/
	fnGetName: (gul: GrowUnitLayer) => string,
): GraphGroup {

	// le label dei layer (es. "Layer 1", "Layer 2", etc) da mostrare nel PLANNER
	const labels = layers.map(layer => fnGetName(layer))

	// posiziono l'ACTIVITY in base alla posizione del LAYER nella GROW-UNIT (row) 
	// e filtro quelle che non riesco ad associare ad un LAYER
	const activitiesPhase = activities.reduce((acc, activity) => {
		const layerNumber = activity.tags?.[TAGS.LAYER_NUMBER] as number
		const growUnitId = activity.tags?.[TAGS.GROWUNIT_ID] as number
		const index = layers.findIndex(layer => layer.layerNumber == layerNumber && layer.growUnitId == growUnitId)
		if (index == -1) return acc
		activity.row = index
		if ( activity.tags == null) activity.tags = {}
		activity.tags[TAGS.GROWUNIT_PHASE] = phase
		acc.push(activity)
		return acc
	}, [] as Activity[])

	return {
		name: phase,
		title: getPhaseLabel(phase),
		collapsed: false,
		noCollap: true,
		noCompact: true,
		labels,
		activities: activitiesPhase,
	} as GraphGroup
}